import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { SignIn } from "@/pages/SignIn";
import { hasSession, ready, signOut } from "@/lib/clerk";
import { RedirectLoop } from "@/pages/RedirectLoop";
import { clearLoadTrace, LOAD_LIMIT, recordLoad } from "@/lib/loopGuard";
import { Button } from "@/components/ui/Button";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { Header } from "@/components/Header";
import { Admin } from "@/pages/Admin";
import { Directory } from "@/pages/Directory";
import { Profile } from "@/pages/Profile";
import { ApiError } from "@/lib/api";
import { useMe, type Me } from "@/lib/me";
import { ThemeProvider } from "@/lib/theme";

/**
 * Copy for each way the gate can refuse. Phase 5 puts the Clerk sign-in screen
 * behind these; until then they render as a plain explanation.
 */
const DENIAL: Record<string, { title: string; body: string }> = {
  unauthenticated: {
    title: "Sign in required",
    body: "Your session has ended. Sign in with your Ley Choon email to continue.",
  },
  deactivated: {
    title: "Access suspended",
    body: "Your access has been suspended. Contact your administrator if you think this is a mistake.",
  },
  forbidden_domain: {
    title: "Ley Choon accounts only",
    body: "This directory is for @leychoon.com accounts. Sign in with your work email.",
  },
  not_registered: {
    title: "Not registered",
    body: "Your account exists but has no role yet. Contact your administrator.",
  },
  no_email: {
    title: "Sign-in misconfigured",
    body: "Your sign-in token carried no email address, so we cannot identify you. Please report this to IT.",
  },
};

/**
 * Local development without a Clerk login.
 *
 * ⚠ Guarded by `import.meta.env.DEV`, which Vite statically replaces with
 * `false` in a production build — so this branch is removed by dead-code
 * elimination and cannot ship enabled, whatever the env var says. Pair it with
 * AUTH_DEV_BYPASS on the API.
 */
const DEV_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_AUTH_DEV_BYPASS === "1";

function denialFor(error: unknown): { title: string; body: string } {
  // ⚠ Only a 401 with no reason may be read as "session ended". A 500 carries
  // no reason either, and telling someone their session ended when the server
  // fell over sends them chasing the wrong problem.
  const reason =
    error instanceof ApiError
      ? (error.reason ?? (error.status === 401 ? "unauthenticated" : null))
      : null;
  return (
    (reason && DENIAL[reason]) ?? {
      title: "Something went wrong",
      body: "We could not confirm your access. Try again in a moment.",
    }
  );
}

function Spinner() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <span className="sr-only">Loading</span>
      <div className="border-border border-t-primary h-6 w-6 animate-spin rounded-full border-2" />
    </div>
  );
}

/**
 * A refusal screen for someone who still holds a Clerk session.
 *
 * ⚠ Deliberately not the SignIn page. Mounting Clerk's widget while a session
 * exists makes it navigate straight to its redirect URL — a full reload that
 * re-runs the refusal, which is a loop. This screen mounts nothing and leaves
 * the next move to the user.
 */
function Refused({ copy }: { copy: { title: string; body: string } }) {
  return (
    <main className="mx-auto grid min-h-dvh max-w-md place-items-center px-6 py-10">
      <div className="w-full text-center">
        <img
          src="/logo.svg"
          alt=""
          width={48}
          height={48}
          className="mx-auto mb-4 rounded-xl"
        />
        <h1 className="text-lg font-semibold">{copy.title}</h1>
        <p className="text-muted-foreground mt-2 text-sm">{copy.body}</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button onClick={() => window.location.reload()}>Try again</Button>
          <Button
            variant="outline"
            onClick={() => {
              void signOut().finally(() => window.location.reload());
            }}
          >
            Sign out
          </Button>
        </div>
      </div>
    </main>
  );
}

function DirectoryPage({ me, query }: { me: Me; query: string }) {
  return (
    <main className="mx-auto max-w-6xl px-5 py-7">
      <AnnouncementBanner />
      <div className="mb-7">
        <h1 className="text-xl font-semibold tracking-tight">
          {me.name ? `Hello, ${me.name.split(" ")[0]}` : "Your apps"}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Every Ley Choon internal app, in one place.
        </p>
      </div>
      <Directory query={query} />
    </main>
  );
}

/**
 * Counted once at module load, before anything mounts Clerk. A reload loop is
 * only visible across page loads, so this cannot live inside a component.
 */
const LOAD_COUNT = recordLoad();

function Shell() {
  const [query, setQuery] = useState("");
  const [clerkState, setClerkState] = useState<"loading" | "session" | "none" | "failed">(
    DEV_BYPASS ? "session" : "loading",
  );
  // The refusal that led to the current sign-out, so the sign-in screen can say
  // why — the query error is gone by the time the session state flips to "none".
  const [refusedWith, setRefusedWith] = useState<{ title: string; body: string } | null>(null);
  // Set when a sign-out completed but the session survived it — the one state
  // where neither the spinner nor the widget is safe to show.
  const [signOutStuck, setSignOutStuck] = useState(false);

  // Layer 2 first: without an active Clerk session there is no token to attach,
  // so there is nothing to ask the API.
  useEffect(() => {
    if (DEV_BYPASS) return;
    // ⚠ Do not touch Clerk while looping — mounting it again is what continues
    // the loop.
    if (LOAD_COUNT >= LOAD_LIMIT) return;
    let cancelled = false;
    void ready()
      .then((clerk) => {
        if (cancelled) return;
        setClerkState(hasSession() ? "session" : "none");
        // Sign-out in another tab, or a session expiring mid-visit, must land
        // the user on sign-in rather than on a page of failing requests.
        clerk.addListener(({ session }) => {
          if (!cancelled) setClerkState(session ? "session" : "none");
        });
      })
      .catch(() => {
        if (!cancelled) setClerkState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const enabled = clerkState === "session";
  const { data: me, isPending, isError, error } = useMe(enabled);

  // The loop guard disarms only once Layer 1 has actually accepted the user.
  // ⚠ Not on "a Clerk session exists" — the worst loop this app has had ran
  // *with* a session on every lap, so clearing on session presence kept the
  // guard blind exactly when it was needed.
  useEffect(() => {
    if (me) clearLoadTrace();
  }, [me]);

  const requiresSignOut =
    isError && error instanceof ApiError && error.requiresSignOut;

  // Layer 1 refused someone Layer 2 accepted. Sign them out of Clerk so they
  // aren't left holding a session the app will never honour — in place, once,
  // and never while rendering the widget.
  useEffect(() => {
    if (!requiresSignOut) return;
    setRefusedWith(denialFor(error));
    void signOut().then(() => {
      if (hasSession()) setSignOutStuck(true);
    });
  }, [requiresSignOut, error]);

  if (!DEV_BYPASS && LOAD_COUNT >= LOAD_LIMIT) return <RedirectLoop />;
  if (clerkState === "loading") return <Spinner />;
  if (clerkState === "failed" || clerkState === "none")
    return <SignIn reason={refusedWith ?? undefined} />;
  if (isPending) return <Spinner />;

  if (isError) {
    // Sign-out is in flight; when it lands, the session listener flips the
    // state to "none" and the sign-in screen explains the refusal.
    if (requiresSignOut && !signOutStuck) return <Spinner />;
    return <Refused copy={denialFor(error)} />;
  }

  return (
    <>
      <Header me={me} query={query} onQueryChange={setQuery} />
      <Routes>
        <Route path="/" element={<DirectoryPage me={me} query={query} />} />
        <Route path="/profile" element={<Profile me={me} />} />
        {/* ⚠ Cosmetic only. The API refuses every admin call from a non-manager
            regardless of what the router allows. */}
        <Route
          path="/admin"
          element={me.isManager ? <Admin me={me} /> : <Navigate to="/" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </ThemeProvider>
  );
}
